/**
 * Notification bell in the rail footer: unread count and a popover listing the member's
 * notifications in this workspace, with a "Needs action" tab for decisions waiting on them (they
 * leave it when the source resolves them). Live updates refetch it within seconds of a change
 * (app/live); the unread count is also polled every minute as a fallback. Opening one marks it read
 * and goes to its page. While impersonating, the server keeps everything unread (read-only session).
 */
import { Bell, CheckCheck, CircleCheck } from "lucide-react";
import { Popover as P } from "radix-ui";
import { useState } from "react";
import { useNavigate } from "react-router";

import { useMarkAllNotificationsRead, useMarkNotificationRead, useNotifications, useUnreadNotifications } from "@/api/queries/notifications";
import type { components } from "@/api/generated/schema";
import { Badge, Button, EmptyState, ErrorState, RelativeTime, SkeletonRows, Tab, TabsContent, TabsList, TabsRoot, Tooltip } from "@/components/ui";
import { cx } from "@/lib/cx";

const PAGE = 20;
type View = "all" | "actions";
type Note = components["schemas"]["Notification"];

export function NotificationBell({ slug, side = "right" }: { slug: string; side?: "right" | "bottom" }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("all");
  const counts = useUnreadNotifications(slug).data;
  const unread = counts?.unread ?? 0;
  const actions = counts?.actions ?? 0;
  const label = [unread ? `Notifications, ${unread} unread` : "Notifications", actions ? `${actions} need${actions === 1 ? "s" : ""} action` : ""]
    .filter(Boolean)
    .join(", ");

  return (
    <P.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setView(actions > 0 ? "actions" : "all");
      }}
    >
      <Tooltip content="Notifications" side={side === "right" ? "right" : "bottom"}>
        <P.Trigger asChild>
          <button
            type="button"
            aria-label={label}
            data-tour="notifications"
            className="relative flex size-9 items-center justify-center rounded-md text-fg-muted transition-colors transition-fast hover:bg-surface-hover hover:text-fg"
          >
            <Bell aria-hidden className="size-[18px]" />
            {unread > 0 && (
              <span
                aria-hidden
                className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] leading-none font-semibold text-accent-fg tabular"
              >
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </button>
        </P.Trigger>
      </Tooltip>
      <P.Portal>
        <P.Content
          side={side}
          align="end"
          sideOffset={8}
          collisionPadding={8}
          aria-label="Notifications"
          className="animate-pop-in z-50 flex max-h-[min(32rem,80dvh)] w-[min(24rem,calc(100vw-1rem))] flex-col rounded-md border border-line bg-surface-raised shadow-lg"
        >
          <TabsRoot value={view} onValueChange={(v) => setView(v as View)} className="flex min-h-0 flex-1 flex-col">
            <div className="flex items-center justify-between gap-2 px-3 pt-1 shadow-[inset_0_-1px_0_var(--border)]">
              <h2 className="sr-only">Notifications</h2>
              <TabsList label="Notifications">
                <Tab value="all">All</Tab>
                <Tab value="actions" count={actions || undefined}>
                  Needs action
                </Tab>
              </TabsList>
              {view === "all" && unread > 0 && <MarkAllRead slug={slug} />}
            </div>
            <TabsContent value="all" className="flex min-h-0 flex-1 flex-col">
              <NotificationList slug={slug} actions={false} open={open} onPicked={() => setOpen(false)} />
            </TabsContent>
            <TabsContent value="actions" className="flex min-h-0 flex-1 flex-col">
              <NotificationList slug={slug} actions open={open} onPicked={() => setOpen(false)} />
            </TabsContent>
          </TabsRoot>
        </P.Content>
      </P.Portal>
    </P.Root>
  );
}

function MarkAllRead({ slug }: { slug: string }) {
  const markAll = useMarkAllNotificationsRead(slug);
  return (
    <Button size="sm" variant="ghost" icon={<CheckCheck aria-hidden className="size-4" />} loading={markAll.isPending} onClick={() => markAll.mutate()}>
      Mark all read
    </Button>
  );
}

function NotificationList({ slug, actions, open, onPicked }: { slug: string; actions: boolean; open: boolean; onPicked: () => void }) {
  const [pageSize, setPageSize] = useState(PAGE);
  const list = useNotifications(slug, { enabled: open, pageSize, actions });
  const markRead = useMarkNotificationRead(slug);
  const navigate = useNavigate();

  const pick = (n: Note) => {
    if (!n.read_at) markRead.mutate(n.id);
    onPicked();
    if (n.link) navigate(`/t/${slug}/${n.link}`);
  };

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {list.isLoading ? (
          <div className="p-3">
            <SkeletonRows rows={3} columns={1} />
          </div>
        ) : list.isError ? (
          <div className="p-3">
            <ErrorState error={list.error} onRetry={() => list.refetch()} />
          </div>
        ) : list.data?.count === 0 ? (
          actions ? (
            <EmptyState icon={<CircleCheck />} title="Nothing needs your action" description="Decisions waiting for you show up here until they're done." />
          ) : (
            <EmptyState icon={<Bell />} title="No notifications" description="Updates about things you asked for show up here." />
          )
        ) : (
          <ul className="flex flex-col divide-y divide-line">
            {list.data?.results.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  className="flex w-full gap-2.5 px-3 py-2.5 text-left transition-colors transition-fast hover:bg-surface-hover focus-visible:bg-surface-hover"
                  onClick={() => pick(n)}
                >
                  <span aria-hidden className={cx("mt-1.5 size-2 shrink-0 rounded-full", n.read_at ? "bg-transparent" : "bg-accent-indicator")} />
                  <span className="min-w-0 flex-1">
                    <span className={cx("block text-sm", n.read_at ? "text-fg-muted" : "font-medium text-fg")}>
                      {!n.read_at && <span className="sr-only">Unread: </span>}
                      {n.title}
                    </span>
                    {n.body && <span className="mt-0.5 block text-xs text-fg-muted">{n.body}</span>}
                    <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-subtle">
                      <RelativeTime value={n.last_occurred_at ?? n.created_at} />
                      {n.occurrences > 1 && <span className="tabular">{n.occurrences} times</span>}
                      {n.requires_action && !n.resolved_at && (
                        <Badge tone="warning" dot={false}>
                          Needs action
                        </Badge>
                      )}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {list.data?.next && pageSize < 100 && (
        <div className="border-t border-line p-2">
          <Button size="sm" variant="ghost" className="w-full" loading={list.isFetching} onClick={() => setPageSize((n) => Math.min(n + PAGE, 100))}>
            Show older
          </Button>
        </div>
      )}
    </>
  );
}
